import type {
  BoxProps,
  IconProps,
  RhombusProps,
  TextProps
} from "../coreComponents/types";
import type { NodeStyle } from "../layoutEngine/layout.types";
import type { SCNode } from "./fsc.types";
import type { BoxNodeProps, RhombusNodeProps } from "./primitiveNodes";

export function createRenderManager() {
  let lastMap: Map<string, SCNodeWithMetaData> | null = null;
  let lastRootNodeKey = "";
  //   const instances: Record<string, CoreComponent> = {};
  function traverse(rootNode: SCNode) {
    const added = new Set<string>();
    const existing = new Set<string>();
    const updated = new Set<string>();
    const moved = new Set<string>();
    const keyMap = new Map<string, SCNodeWithMetaData>();
    let isFirstRender = false;

    if (rootNode.key !== lastRootNodeKey) {
      isFirstRender = true;
      lastMap?.clear();
    }

    const lastMapKeys = new Set(lastMap?.keys() || []);

    function walk({
      node,
      parentKey,
      nextNodeKey,
      index
    }: {
      node: SCNode;
      parentKey: string | null;
      nextNodeKey: string | null;
      index: number;
    }) {
      const nodeWithMetaData = addMetaData({
        node,
        parentKey,
        nextNodeKey,
        index
      });
      keyMap.set(node.key, nodeWithMetaData);
      const lastHashedNode = lastMap?.get(node.key);
      if (lastHashedNode) {
        const changes = getChanges({
          lastNode: lastHashedNode,
          currentNode: nodeWithMetaData
        });

        if (changes.doesNeedsToUpdate) {
          updated.add(node.key);
        }

        if (changes.doesNeedsToMove) {
          moved.add(node.key);
        }
        existing.add(node.key);
        lastMapKeys.delete(node.key);
      } else {
        added.add(node.key);
        // updated.add(node.key);
      }

      if (node.children.length) {
        node.children.forEach((child, index) =>
          walk({
            node: child,
            parentKey: node.key,
            nextNodeKey: node.children[index + 1]?.key || null,
            index
          })
        );
      }
    }

    walk({
      node: rootNode,
      parentKey: null,
      nextNodeKey: null,
      index: 0
    });

    lastMap = keyMap;
    lastRootNodeKey = rootNode.key;

    return {
      firstRender: isFirstRender,
      added,
      existing,
      updated,
      moved,
      removed: lastMapKeys,
      keyMap
    };
  }

  function destroy() {
    lastMap?.clear();
  }

  return {
    getNodeForKey: (key: string) => lastMap?.get(key),
    getRootNodeKey: () => lastRootNodeKey,
    traverse,
    destroy
  };
}

export type SCNodeWithMetaData = SCNode & {
  yogaStyleFP: string;
  serializablePropsFP: string;
  isInteractive: boolean;
  parentKey: string | null;
  nextNodeKey: string | null;
  index: number;
};

function addMetaData({
  node,
  parentKey,
  nextNodeKey,
  index
}: {
  node: SCNode;
  parentKey: string | null;
  nextNodeKey: string | null;
  index: number;
}): SCNodeWithMetaData {
  const { yogaStyleFP, serializablePropsFP, isInteractive } =
    getPropsHash(node);
  return {
    ...node,
    yogaStyleFP,
    serializablePropsFP,
    isInteractive,
    parentKey,
    nextNodeKey,
    index
  };
}

function getChanges({
  lastNode,
  currentNode
}: {
  lastNode: SCNodeWithMetaData;
  currentNode: SCNodeWithMetaData;
}) {
  // TODO: It might be possible to optimize this further
  // to process type of update separately.
  // For example, an update where yoga style changes, doesn't necessarily
  // need to call "updateProps" method, if we update the yoga node's styles
  // during rendering.

  let doesNeedsToMove = false;
  let doesNeedsToUpdate = false;

  if (
    lastNode.yogaStyleFP !== currentNode.yogaStyleFP ||
    lastNode.serializablePropsFP !== currentNode.serializablePropsFP ||
    currentNode.isInteractive ||
    lastNode.isInteractive // because if callbacks are removed, then we need to update as well
  ) {
    doesNeedsToUpdate = true;
  }

  // TODO: Edge cases are not handled.
  // Those edge cases will show up if we implement
  // multi-select drag and drop. So if someone selected
  // two consecutive nodes and moved them to a different
  // location within same parent,
  // then this logic will fail.
  if (
    lastNode.parentKey !== currentNode.parentKey ||
    (lastNode.index !== currentNode.index &&
      lastNode.nextNodeKey !== currentNode.nextNodeKey)
  ) {
    doesNeedsToMove = true;
  }

  return {
    doesNeedsToUpdate,
    doesNeedsToMove
  };
}

function getPropsHash(node: SCNode) {
  switch (node.componentKey) {
    case "Box": {
      const props = node.props as BoxNodeProps;
      return generateBoxPropsHash(props);
    }
    case "Rhombus": {
      const props = node.props as RhombusNodeProps;
      return generateRhombusPropsHash(props);
    }
    case "Text": {
      const props = node.props as TextProps;
      return generateTextPropsHash(props);
    }
    case "Icon": {
      const props = node.props as IconProps;
      return generateIconPropsHash(props);
    }
    case "Line":
    default:
      return {
        yogaStyleFP: "",
        serializablePropsFP: "",
        isInteractive: false
      };
  }
}

const yogaStyleFPProps = [
  "width",
  "maxWidth",
  "minWidth",
  "height",
  "maxHeight",
  "minHeight",
  "paddingTop",
  "paddingBottom",
  "paddingLeft",
  "paddingRight",
  "marginTop",
  "marginBottom",
  "marginLeft",
  "marginRight",
  "alignContent",
  "alignItems",
  "alignSelf",
  "justifyContent",
  "aspectRatio",
  "flexBasis",
  "flexGrow",
  "flexShrink",
  "flexWrap",
  "flexDirection",
  "gapRow",
  "gapColumn",
  "overflow",
  "position",
  "left",
  "top",
  "right",
  "bottom",
  "visible",
  "borderWidth"
  // Non-layout styles

  //   "hover.backgroundColor" // handled separately
];

const boxNonYogaStyleFPProps = [
  "backgroundColor",
  "borderColor",
  "borderRadius",
  "cursor"
];

function getYogaStyleHash(style: Partial<NodeStyle>) {
  let yogaStyleFP = "";

  for (const prop of yogaStyleFPProps) {
    yogaStyleFP += `${style[prop as keyof NodeStyle] ?? "-"} `;
  }

  return yogaStyleFP;
}

function generateTextPropsHash(props: TextProps) {
  const style = props.style || {};
  const textStyle = props.textStyle || {};

  let serializablePropsFP = `${props.text ?? ""} `;

  serializablePropsFP += `${textStyle.color ?? "-"} `;
  serializablePropsFP += `${textStyle.fontSize ?? "-"} `;
  serializablePropsFP += `${textStyle.fontWeight ?? "-"} `;
  serializablePropsFP += `${textStyle.fontFamily ?? "-"}`;
  serializablePropsFP += `${textStyle.italic ?? "-"}`;
  serializablePropsFP += `${textStyle.fontFamily ?? "-"}`;

  return {
    yogaStyleFP: getYogaStyleHash(style),
    serializablePropsFP,
    isInteractive: false
  };
}

function generateIconPropsHash(props: IconProps) {
  let serializablePropsFP = `${props.icon ?? ""}`;
  serializablePropsFP += `${props.color ?? ""}`;
  serializablePropsFP += `${props.size ?? ""}`;

  return {
    yogaStyleFP: getYogaStyleHash(props.style || {}),
    serializablePropsFP,
    isInteractive: false
  };
}

function generateBoxPropsHash(props: BoxProps) {
  const style = props.style || {};
  if (props.eventHandlers) {
    // short circuit if there are event handlers, because
    // at that point we don't care about comparing props,
    // and all nodes with event handlers should be updated
    // ALTHOUGH this business logic shouldn't be inside this
    // function as this function should NOT know about how
    // the values are being used, but currently this saves us
    // some compute, specially when dealing with large number
    // of nodes, so that's why this is here for now.
    return {
      yogaStyleFP: "",
      serializablePropsFP: "",
      isInteractive: true
    };
  }
  let serializablePropsFP = "";
  for (const prop of boxNonYogaStyleFPProps) {
    serializablePropsFP += `${style[prop as keyof NodeStyle] ?? "-"} `;
  }
  serializablePropsFP += `${style.hover?.backgroundColor ?? "-"} `;

  serializablePropsFP += `${props.dragType ?? "-"} `;
  serializablePropsFP += `${props.dropTypes?.join(",") ?? "-"} `;
  serializablePropsFP += `${props.dragHandleFor ?? "-"} `;
  serializablePropsFP += `${JSON.stringify(props.variables ?? "{}")}`;

  return {
    yogaStyleFP: getYogaStyleHash(style),
    serializablePropsFP,
    isInteractive: !!props.eventHandlers
  };
}

function generateRhombusPropsHash(props: RhombusProps) {
  const style = props.style || {};
  if (props.eventHandlers) {
    // short circuit if there are event handlers, because
    // at that point we don't care about comparing props,
    // and all nodes with event handlers should be updated
    // ALTHOUGH this business logic shouldn't be inside this
    // function as this function should NOT know about how
    // the values are being used, but currently this saves us
    // some compute, specially when dealing with large number
    // of nodes, so that's why this is here for now.
    return {
      yogaStyleFP: "",
      serializablePropsFP: "",
      isInteractive: true
    };
  }
  let serializablePropsFP = "";
  for (const prop of boxNonYogaStyleFPProps) {
    serializablePropsFP += `${style[prop as keyof NodeStyle] ?? "-"} `;
  }
  serializablePropsFP += `${style.hover?.backgroundColor ?? "-"} `;
  serializablePropsFP += `${JSON.stringify(props.variables ?? "{}")}`;

  return {
    yogaStyleFP: getYogaStyleHash(style),
    serializablePropsFP,
    isInteractive: !!props.eventHandlers
  };
}
